
import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useProject } from "@/hooks/useProject";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface EditClientDialogProps {
  client: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditClientDialog({ client, open, onOpenChange }: EditClientDialogProps) {
  const [nombre, setNombre] = useState(client.nombre);
  const [mision, setMision] = useState(client.mision || '');
  const [file, setFile] = useState<File | null>(null);
  const queryClient = useQueryClient();
  const { projectClient, currentProject } = useProject();

  useEffect(() => {
    setNombre(client.nombre);
    setMision(client.mision || '');
  }, [client]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let logoUrl = client.logo;
      
      if (file) {
        // Extract old filename from URL if it exists
        const oldFileName = client.logo ? client.logo.split('/').pop() : null;
        
        // Delete old logo if exists
        if (oldFileName) {
          await supabase.storage
            .from('client-logos')
            .remove([oldFileName]);
        }
        
        // Upload new logo
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('client-logos')
          .upload(fileName, file);
          
        if (uploadError) throw uploadError;
        
        // Get the public URL
        const { data: { publicUrl } } = supabase.storage
          .from('client-logos')
          .getPublicUrl(fileName);
          
        logoUrl = publicUrl;
      }
      
      const { error } = await projectClient
        .from('clientes')
        .update({ 
          nombre,
          mision,
          logo: logoUrl
        })
        .eq('id', client.id)
        .eq('project_id', projectClient.getProjectId());
      
      if (error) throw error;
      
      toast.success('Cliente actualizado correctamente');
      queryClient.invalidateQueries({ queryKey: ['clients', currentProject] });
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Cliente</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nombre">Nombre</Label>
            <Input
              id="nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre del cliente"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mision">Misión</Label>
            <Textarea
              id="mision"
              value={mision}
              onChange={(e) => setMision(e.target.value)}
              placeholder="Misión del cliente"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="logo">Logo</Label>
            <Input
              id="logo"
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {client.logo && (
              <img
                src={client.logo}
                alt={`Logo actual de ${client.nombre}`}
                className="w-20 h-20 object-contain"
              />
            )}
          </div>
          <Button type="submit">Actualizar Cliente</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
